import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { Combatant, PlayerView, ServerMessage } from '@sd/shared';
import { EncounterConnection } from '../lib/ws.js';
import { TacticalMap } from '../components/combat/TacticalMap.js';
import { PlayerQuickMenu } from '../components/combat/QuickMenu.js';
import { CONDITIONS } from '../components/combat/conditions.js';
import { useFxQueue } from '../hooks/useFxQueue.js';
import { useTheme, themeClass } from '../hooks/useTheme.js';
import { DiceRoller, type DiceRollResult } from '../components/DiceRoller.js';
import { D20Roller } from '../components/D20Roller.js';
import { PipBar, nextMarks } from '../components/combat/PipBar.js';
import { ThemeSwitch } from '../components/ThemeSwitch.js';
import '../styles/encounter.css';
import '../styles/combat.css';

/**
 * Player-екран, компактний — все на одну viewport-висоту.
 * Шар «player-view» обмежує height: 100vh, log + side-pane скролять усередині.
 */
export function RoomPlay(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const sessionId = typeof window !== 'undefined' ? localStorage.getItem(`sd:player-session:${id}`) : null;
  const [view, setView] = useState<PlayerView | null>(null);
  const [conn, setConn] = useState<'connecting' | 'open' | 'closed' | 'error'>('connecting');
  const wsRef = useRef<EncounterConnection | null>(null);
  const fx = useFxQueue();
  const fxPush = fx.push;
  const { theme } = useTheme();
  const lastLogAtRef = useRef<number>(0);
  const [sideTab, setSideTab] = useState<'stats' | 'actions' | 'features'>('actions');
  /** Поточна ціль для атак — id ворога/союзника (НЕ свій combatant) */
  const [targetId, setTargetId] = useState<string | null>(null);

  type DiceConfig =
    | { kind: 'duality'; modifier: number; adv: 'none' | 'advantage' | 'disadvantage'; label?: string;
        autoRoll: boolean;
        playback?: { hope: number; fear: number; advDie?: number; modifier?: number } }
    | { kind: 'd20'; modifier: number; label?: string; autoRoll: boolean;
        playback?: { dice: number[]; pickedIndex: number; modifier?: number } };
  const [diceConfig, setDiceConfig] = useState<DiceConfig | null>(null);

  const openDice = useCallback((opts: { modifier?: number; adv?: 'none' | 'advantage' | 'disadvantage'; label?: string; autoRoll?: boolean }) => {
    setDiceConfig({
      kind: 'duality',
      modifier: opts.modifier ?? 0,
      adv: opts.adv ?? 'none',
      label: opts.label,
      autoRoll: opts.autoRoll ?? false,
    });
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const c = new EncounterConnection({
      encounterId: id,
      role: 'player',
      credential: sessionId,
      onMessage: (m: ServerMessage) => {
        if (m.type === 'state:full' || m.type === 'state:patch') {
          setView(m.payload as PlayerView);
        }
      },
      onStatus: setConn,
    });
    wsRef.current = c;
    return () => c.close();
  }, [id, sessionId]);

  // Підписка на нові log-події з fx + playback dice
  useEffect(() => {
    if (!view) return;
    const log = view.recentLog;
    if (!log.length) return;
    const newEntries = log.filter((e) => e.at > lastLogAtRef.current);
    for (const e of newEntries) {
      if (e.fx && e.targetId) fxPush(e.fx, e.targetId, { value: e.value, fromId: e.fromId, projKind: e.projKind });
      if (e.kind === 'roll' && e.roll && !diceConfig?.autoRoll) {
        const r = e.roll;
        const label = r.label ? `${r.label} (${e.who ?? 'roll'})` : (e.who ?? 'Roll');
        if (r.kind === 'd20' && r.dice && r.pickedIndex !== undefined) {
          setDiceConfig({
            kind: 'd20',
            modifier: r.modifier ?? 0,
            label,
            autoRoll: false,
            playback: { dice: r.dice, pickedIndex: r.pickedIndex, modifier: r.modifier },
          });
        } else if (r.hope !== undefined && r.fear !== undefined) {
          setDiceConfig({
            kind: 'duality',
            modifier: r.modifier ?? 0,
            adv: r.advDie ? (r.advDie > 0 ? 'advantage' : 'disadvantage') : 'none',
            label,
            autoRoll: false,
            playback: { hope: r.hope, fear: r.fear, advDie: r.advDie, modifier: r.modifier },
          });
        }
      }
    }
    const maxAt = log[log.length - 1]?.at ?? 0;
    if (maxAt > lastLogAtRef.current) lastLogAtRef.current = maxAt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, fxPush]);

  if (!sessionId) return <Navigate to={`/room/${id}/join`} replace />;

  if (!view) {
    return (
      <div className="encounter-shell player-view combat-fit">
        <div className="encounter-body" style={{ color: 'var(--text-dim)' }}>
          підключаюсь… <span className={`conn-pill ${conn}`}>{conn}</span>
        </div>
      </div>
    );
  }

  const own = view.ownCombatant;
  const ws = wsRef.current;
  const send = (msg: Parameters<NonNullable<typeof ws>['send']>[0]): void => { ws?.send(msg); };

  const canMove = (cid: string): boolean => own?.id === cid;
  const allTokens = own
    ? [own, ...view.others.map((o) => ({ ...o }))]
    : view.others;

  return (
    <div className="encounter-shell player-view combat-fit">
      <div className={`combat-page ${themeClass(theme)}`.trim()}>
        {/* Top — все компактно в один ряд */}
        <div className="combat-toolbar">
          <CompactFear value={view.encounter.fear} />
          <div style={{ flex: 1, fontFamily: 'Cormorant Garamond, serif', fontSize: 16, color: 'var(--gold-bright)', display: 'flex', alignItems: 'center', paddingLeft: 14 }}>
            {view.encounter.name}
            <span style={{ fontSize: 10, color: 'var(--text-dim)', marginLeft: 12, fontFamily: 'Spline Sans Mono, monospace' }}>
              {own ? own.name : ''}
            </span>
          </div>
          <ThemeSwitch compact />
          <button
            onClick={() => openDice({ autoRoll: false })}
            title="Кидок 2d12"
            style={{
              padding: '5px 10px', marginLeft: 4,
              background: 'var(--bg-card)', border: '1px solid var(--gold-dim)',
              color: 'var(--gold)', borderRadius: 4, cursor: 'pointer',
              fontFamily: 'Cormorant Garamond, serif', fontSize: 11,
              textTransform: 'uppercase', letterSpacing: '0.1em',
            }}
          >Кости</button>
          <span className={`conn-pill ${conn}`} style={{ marginLeft: 8 }}>{conn}</span>
        </div>

        {view.encounter.countdowns.length > 0 && (
          <div className="counters-bar">
            {view.encounter.countdowns.map((c) => (
              <div key={c.id} className="counter">
                <span className="ctr-label">{c.label}</span>
                <div className="ctr-pips">
                  {Array.from({ length: c.max }, (_, i) => (
                    <div key={i} className={`ctr-pip ${i < c.current ? 'on' : ''}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="combat-main">
          <div className="pane-map">
            <TacticalMap
              map={view.encounter.map}
              combatants={allTokens}
              canMove={canMove}
              onMove={(cid, x, y) => send({ type: 'token:move', payload: { id: cid, x, y } })}
              fxs={fx.fxs}
              targetId={targetId}
              // Player не може targetити сам себе
              onSetTarget={(id) => setTargetId(id === own?.id ? null : id)}
              renderQuickMenu={(t, close) => (
                <PlayerQuickMenu
                  token={t}
                  close={close}
                  isOwn={t.id === own?.id}
                  isTarget={targetId === t.id}
                  onTarget={() => setTargetId(targetId === t.id ? null : t.id)}
                  onConditionToggle={(cond, on) => {
                    if (own?.id !== t.id) return;
                    send({ type: 'combatant:condition', payload: { id: t.id, condition: cond, on } });
                  }}
                  onActions={() => setSideTab('actions')}
                  onFeatures={() => setSideTab('features')}
                />
              )}
            />
          </div>
          <div className="pane-side">
            <div className="side-tabs">
              <button className={`side-tab ${sideTab === 'stats' ? 'active' : ''}`} onClick={() => setSideTab('stats')}>Стан</button>
              <button className={`side-tab ${sideTab === 'actions' ? 'active' : ''}`} onClick={() => setSideTab('actions')}>Дії</button>
              <button className={`side-tab ${sideTab === 'features' ? 'active' : ''}`} onClick={() => setSideTab('features')}>Здібності</button>
            </div>
            {own ? (
              <>
                {sideTab === 'stats' && (
                  <OwnStats
                    own={own}
                    onConditionToggle={(type, on) => send({ type: 'combatant:condition', payload: { id: own.id, condition: type as Combatant['conditions'][number], on } })}
                    onStatSet={(field, value) => send({ type: 'combatant:stat', payload: { id: own.id, field, value } })}
                  />
                )}
                {sideTab === 'actions' && (
                  <OwnActions
                    own={own}
                    targetId={targetId}
                    targetName={targetId ? allTokens.find((t) => t.id === targetId)?.name : undefined}
                    onClearTarget={() => setTargetId(null)}
                    onActionRoll={(modifier, adv, label, projKind) => {
                      const payload: Parameters<NonNullable<typeof ws>['send']>[0] = {
                        type: 'dice:roll',
                        payload: {
                          kind: 'duality',
                          modifier,
                          advantage: adv,
                          label,
                          fromId: own?.id,
                          toId: targetId ?? undefined,
                          projKind,
                        },
                      };
                      // Залишаємо локальний modal-show, але передаємо target через onRollComplete
                      openDice({ modifier, adv, label, autoRoll: true });
                      // Pre-emit немає — фінальний send відбудеться в onRollComplete
                      void payload;
                    }}
                    onDamageRoll={(formula, label, damageType, projKind) => send({
                      type: 'damage:roll',
                      payload: { formula, label, damageType, fromId: own?.id, toId: targetId ?? undefined },
                    })}
                  />
                )}
                {sideTab === 'features' && <OwnFeatures own={own} />}
              </>
            ) : (
              <p style={{ color: 'var(--text-dim)' }}>Не знайдено героя</p>
            )}
          </div>
        </div>

        <div className="log-panel">
          {view.recentLog.length === 0 && <div style={{ color: 'var(--text-dim)', fontStyle: 'italic' }}>Журнал порожній</div>}
          {view.recentLog.slice(-20).map((e) => (
            <div key={e.id} className={`entry ${e.kind ?? ''}`}>
              <span className="time">{new Date(e.at * 1000).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}</span>
              {e.who && <span style={{ color: 'var(--gold-dim)' }}>{e.who}: </span>}
              {e.text}
            </div>
          ))}
        </div>
      </div>

      {diceConfig && (
        <div
          onClick={() => setDiceConfig(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 6, padding: 24, minWidth: 480, maxWidth: 560,
              boxShadow: 'var(--shadow-deep)',
            }}
          >
            {diceConfig.kind === 'd20' ? (
              // Player завжди отримує d20-modal лише як PLAYBACK (свої кубки — 2d12).
              <D20Roller
                label={diceConfig.label}
                initialModifier={diceConfig.modifier}
                autoRoll={false}
                playback={diceConfig.playback}
                onClose={() => setDiceConfig(null)}
              />
            ) : (
              <DiceRoller
                label={diceConfig.label}
                initialModifier={diceConfig.modifier}
                autoRoll={diceConfig.autoRoll}
                playback={diceConfig.playback}
                onClose={() => setDiceConfig(null)}
                onRollComplete={(result: DiceRollResult) => {
                  if (diceConfig.autoRoll && !diceConfig.playback) {
                    // Pick projKind from label (Атака → melee/ranged/spell)
                    const projKind: 'arrow' | 'spell' | 'melee' =
                      /лук|стріл|кидальн|спис/i.test(diceConfig.label ?? '') ? 'arrow'
                      : /магі|спел|жезл|посох|закля/i.test(diceConfig.label ?? '') ? 'spell'
                      : 'melee';
                    send({
                      type: 'dice:roll',
                      payload: {
                        kind: 'duality',
                        modifier: result.modifier,
                        advantage: result.advDie ? (result.advDie > 0 ? 'advantage' : 'disadvantage') : 'none',
                        result: { hope: result.hope, fear: result.fear, advDie: result.advDie },
                        label: diceConfig.label,
                        fromId: own?.id,
                        toId: targetId ?? undefined,
                        projKind,
                      },
                    });
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CompactFear({ value }: { value: number }): JSX.Element {
  return (
    <div className="fear-pill" style={{ minWidth: 160, padding: '6px 10px' }}>
      <div className="fp-top">Страх GM</div>
      <div className="fp-bar">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i} className={`fp-seg ${i < value ? 'filled' : ''}`} style={{ height: 10 }} />
        ))}
      </div>
      <div className="fp-controls" style={{ justifyContent: 'center' }}>
        <span className="fp-value" style={{ fontSize: 18 }}>{value}/12</span>
      </div>
    </div>
  );
}

function OwnStats({ own, onConditionToggle, onStatSet }: {
  own: Combatant;
  onConditionToggle: (type: string, on: boolean) => void;
  onStatSet: (field: 'hp' | 'stress' | 'armor', value: number) => void;
}): JSX.Element {
  return (
    <div>
      <h3 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--gold-bright)', margin: '0 0 10px', fontSize: 16 }}>
        {own.name}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <StatRow label="Шкода (HP)" marked={own.stats.hp} max={own.stats.hpMax} kind="hp" onSet={(v) => onStatSet('hp', v)} />
        <StatRow label="Стрес" marked={own.stats.stress} max={own.stats.stressMax} kind="stress" onSet={(v) => onStatSet('stress', v)} />
        {own.stats.armorMax > 0 && (
          <StatRow label="Броня" marked={own.stats.armor} max={own.stats.armorMax} kind="armor" onSet={(v) => onStatSet('armor', v)} />
        )}
      </div>
      <div className="conditions-row" style={{ marginTop: 10 }}>
        {CONDITIONS.map((def) => {
          const on = own.conditions.includes(def.type);
          return (
            <button
              key={def.type}
              className={`cond-btn ${on ? 'on' : ''}`}
              onClick={() => onConditionToggle(def.type, !on)}
              title={def.label}
            >{def.short}</button>
          );
        })}
      </div>
      {own.stats.hp >= own.stats.hpMax && (
        <div style={{
          marginTop: 10, padding: '6px 10px',
          background: 'rgba(157,79,74,0.15)', color: 'var(--blood-bright)',
          fontFamily: 'Cormorant Garamond, serif', fontSize: 12,
          textAlign: 'center', borderRadius: 3,
        }}>🩸 ПОВАЛЕНИЙ</div>
      )}
    </div>
  );
}

function OwnActions({ own, targetId, targetName, onClearTarget, onActionRoll, onDamageRoll }: {
  own: Combatant;
  targetId: string | null;
  targetName?: string;
  onClearTarget: () => void;
  onActionRoll: (modifier: number, adv: 'none' | 'advantage' | 'disadvantage', label?: string, projKind?: 'arrow' | 'spell' | 'melee') => void;
  onDamageRoll: (formula: string, label?: string, damageType?: string, projKind?: 'arrow' | 'spell' | 'melee') => void;
}): JSX.Element {
  const projFor = (atkName: string | undefined): 'arrow' | 'spell' | 'melee' => {
    if (!atkName) return 'melee';
    if (/лук|стріл|кидальн|спис/i.test(atkName)) return 'arrow';
    if (/магі|спел|жезл|посох|закля/i.test(atkName)) return 'spell';
    return 'melee';
  };
  return (
    <div>
      {/* Target banner */}
      <div style={{
        display: 'flex', gap: 6, alignItems: 'center',
        padding: '5px 8px', marginBottom: 8,
        background: targetId ? 'rgba(157,79,74,0.15)' : 'var(--bg-deep)',
        border: `1px dashed ${targetId ? 'var(--blood-bright)' : 'var(--border)'}`,
        borderRadius: 3,
        fontFamily: 'Spline Sans Mono, monospace', fontSize: 10,
      }}>
        <span style={{ color: 'var(--text-dim)' }}>Ціль:</span>
        <span style={{ color: targetId ? 'var(--blood-bright)' : 'var(--text-dim)', flex: 1, fontFamily: 'Cormorant Garamond, serif' }}>
          {targetName ?? '— клікни ворога на мапі —'}
        </span>
        {targetId && (
          <button
            onClick={onClearTarget}
            style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 14, padding: 0 }}
            title="Скасувати ціль"
          >×</button>
        )}
      </div>

      <div style={{
        fontFamily: 'Cormorant Garamond, serif', fontSize: 10, textTransform: 'uppercase',
        letterSpacing: '0.14em', color: 'var(--gold)', marginBottom: 4,
      }}>Trait checks</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3 }}>
        {(['Agility', 'Strength', 'Finesse', 'Instinct', 'Presence', 'Knowledge'] as const).map((trait) => (
          <button
            key={trait}
            className="cond-btn"
            style={{ fontSize: 9, padding: '4px 4px' }}
            onClick={() => onActionRoll(0, 'none', `${trait}`)}
            title={`Action roll з ${trait}`}
          >{trait}</button>
        ))}
      </div>

      {own.stats.attacks.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif', fontSize: 10, textTransform: 'uppercase',
            letterSpacing: '0.14em', color: 'var(--gold)', marginBottom: 4,
          }}>Зброя</div>
          {own.stats.attacks.map((a, i) => (
            <div key={i} className="quick-row">
              <div>
                <div className="label">{a.name ?? '—'}{a.primary ? ' ✦' : ''}</div>
                {(a.range || a.damage) && (
                  <div className="info">
                    {a.range ?? ''}{a.range && a.damage ? ' · ' : ''}{a.damage ?? ''}{a.damageType ? ` ${a.damageType}` : ''}
                  </div>
                )}
              </div>
              <button
                className="quick-btn"
                onClick={() => onActionRoll(own.stats.attackMod ?? 0, 'none', `Атака: ${a.name}`, projFor(a.name))}
                title={targetId ? `Action roll + arc fx до цілі` : `Action roll без цілі (без анімації стріли)`}
              >Атк</button>
              {a.damage && (
                <button
                  className="quick-btn dmg"
                  onClick={() => onDamageRoll(a.damage!, `Damage: ${a.name}`, a.damageType, projFor(a.name))}
                  title={`Roll ${a.damage}${targetId ? ' + hit fx на цілі' : ''}`}
                >{a.damage}</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OwnFeatures({ own }: { own: Combatant }): JSX.Element {
  const features = own.stats.features ?? [];
  if (features.length === 0) return <p style={{ color: 'var(--text-dim)', fontSize: 12 }}>Особливостей не задано</p>;
  return (
    <div>
      <div style={{
        fontFamily: 'Cormorant Garamond, serif', fontSize: 10, textTransform: 'uppercase',
        letterSpacing: '0.14em', color: 'var(--gold)', marginBottom: 6,
      }}>Здібності</div>
      {features.map((f, i) => (
        <details key={i} style={{ padding: '4px 0', borderBottom: i < features.length - 1 ? '1px dotted var(--border)' : 'none', fontSize: 12 }}>
          <summary style={{
            cursor: 'pointer', fontFamily: 'Cormorant Garamond, serif',
            color: 'var(--gold)', fontWeight: 600,
            listStyle: 'none', display: 'flex', justifyContent: 'space-between',
          }}>
            <span>{f.name}</span>
            <span style={{ fontSize: 9, color: 'var(--text-dim)' }}>▾</span>
          </summary>
          {f.description && (
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 11, lineHeight: 1.45 }}>
              {f.description}
            </p>
          )}
        </details>
      ))}
    </div>
  );
}

function StatRow({ label, marked, max, kind, onSet }: {
  label: string;
  marked: number;
  max: number;
  kind: 'hp' | 'stress' | 'armor';
  onSet: (v: number) => void;
}): JSX.Element {
  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontFamily: 'Spline Sans Mono, monospace', fontSize: 9,
        textTransform: 'uppercase', letterSpacing: '0.12em',
        color: 'var(--text-dim)', marginBottom: 2,
      }}>
        <span>{label}</span>
        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 11, color: marked >= max ? 'var(--blood-bright)' : 'var(--gold)' }}>
          {marked}/{max}
        </span>
      </div>
      <PipBar
        marked={marked}
        max={max}
        kind={kind}
        size="sm"
        onToggle={(i, wasMarked) => onSet(nextMarks(marked, i, wasMarked))}
      />
    </div>
  );
}
